import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";

const workflowType = z.union([
  z.literal("pricing"),
  z.literal("sp"),
  z.literal("ads"),
  z.literal("dsp"),
  z.literal("wm"),
  z.literal("ntm"),
  z.literal("both"),
]);
export type WorkflowType = z.infer<typeof workflowType>;

const report_status = z.union([
  z.literal("P"),
  z.literal("S"),
  z.literal("F"),
  z.literal("K"),
  z.literal("E"),
  z.literal("N/A"),
]);
export type ReportStatus = z.infer<typeof report_status>;

const workflowSchema = z.object({
  id: z.string(),
  tracking_id: z.string(),
  tracking_context: z.object({
    org_id: z.number(),
  }),
  status: z.union([
    z.literal("PENDING"),
    z.literal("RUNNING"),
    z.literal("SUCCESS"),
    z.literal("FAILED"),
    z.literal("CANCELLED"),
  ]),
  is_finished: z.boolean(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),

  task_context: z.object({
    tasks: z.record(
      z.string(),
      z.object({
        job_id: z.string(),
        start_time: z.string(),
      }),
    ),
    download_listings_tasks: z.record(z.string(), z.string().datetime().nullable()),
    pending_reports: z.record(
      z.string(),
      z.array(
        z.object({
          id: z.number(),
          plan: z.number(),
          entryId: z.number().optional(),
          credential: z.unknown(),
          pending_report: z
            .object({
              id: z.number(),
              report_type: z.string(),
              status: report_status,
              credential: z.number(),
              wait_until: z.string().datetime().nullable(),
              payload: z.object({
                marketplace_ids: z.string().array().optional(),
                execution_options: z
                  .object({
                    reportDate: z.string().optional().nullable(),
                    reportType: z.string().optional(),
                    startDate: z.string().optional().nullable(),
                    endDate: z.string().optional().nullable(),
                    dataEndTime: z.string().date().optional().nullable(),
                    dataStartTime: z.string().date().optional().nullable(),
                    reportOptions: z.unknown().nullable(),
                    marketplaceIds: z.string().array().optional(),
                  })
                  .optional(),
                report_id: z.string().optional().nullable(),
              }),
              warehouse: z
                .object({
                  id: z.number(),
                })
                .nullable(),
            })
            .passthrough(),
        }),
      ),
    ),
  }),
});
export type Workflow = z.infer<typeof workflowSchema>;

export const workflowsApiDefinitions = prefixApi(
  "",
  apiBuilder({
    alias: "startWorkflow",
    method: "post",
    path: "/workflows/start/",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ wft: workflowType }),
      },
    ],
    status: 200,
    response: z.object({ status: z.literal("success") }),
  })
    .addEndpoint({
      alias: "getWorkflows",
      method: "get",
      path: "/api/workflow/",
      parameters: [{ name: "page_size", type: "Query", schema: z.number() }],
      status: 200,
      response: z.object({
        count: z.number(),
        next: z.unknown(),
        previous: z.unknown(),
        results: workflowSchema.array(),
      }),
    })
    .addEndpoint({
      alias: "getWorkflowState",
      method: "get",
      path: "/api/workflows/:trackingId/",
      status: 200,
      response: z.object({
        workflow: z.object({
          callbacks: z.unknown(),
          directed: z.boolean(),
          graph: z.unknown(),
          links: z.unknown().array(),
          multigraph: z.boolean(),
          nodes: z.unknown().array(),
        }),
        state: z.object({
          status: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
        }),
        job_states: z.record(
          z.string(),
          z.object({
            status: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
            retries: z.number(),
            context: z.unknown(),
            exception: z.unknown().nullable(),
          }),
        ),
        outputs: z.object({}),
      }),
    })
    .addEndpoint({
      alias: "getWorkflowProgress",
      method: "get",
      path: "/api/workflows/:trackingId/progress",
      status: 200,
      response: z.object({
        status: z.number(),
        progress: z.tuple([z.number(), z.number()]),
      }),
    })
    .build(),
);
